/**
 * Recovery banner shown above the MAIN bracket when the draw has reserved
 * qualifier positions but no QUALIFYING structure exists in
 * `drawDefinition.structures`. This state is uncommon — normally the
 * qualifying placeholder is created at draw-generation time and appears in
 * the structure dropdown — but if the placeholder has been removed (or the
 * draw was generated by an older path), the structure dropdown won't list
 * "Qualifying" and users have no obvious path back. The banner gives them
 * a one-click recovery: open the addDraw drawer pre-filled by
 * `computeGenerateQualifying` and submit.
 *
 * Conditions for showing:
 *   - selected structure is MAIN (stageSequence 1)
 *   - main has at least one positionAssignment with `qualifier: true`
 *   - drawDefinition has no QUALIFYING structure
 */
import { navigateToEvent } from 'components/tables/common/navigateToEvent';
import { drawDefinitionConstants } from 'tods-competition-factory';
import { addDraw } from 'components/drawers/addDraw/addDraw';

const { MAIN, QUALIFYING } = drawDefinitionConstants;

const BANNER_ID = 'generateQualifyingBanner';

export function clearGenerateQualifyingBanner(container: HTMLElement | null | undefined): void {
  if (!container) return;
  container.querySelector(`#${BANNER_ID}`)?.remove();
}

export function maybeRenderGenerateQualifyingBanner({
  container,
  drawDefinition,
  stage,
  drawId,
  eventId,
  drawName,
  callback,
}: {
  container: HTMLElement | null | undefined;
  drawDefinition: any;
  stage: string;
  drawId: string;
  eventId: string;
  drawName?: string;
  callback?: () => void;
}): void {
  if (!container || stage !== MAIN) return;
  clearGenerateQualifyingBanner(container);

  const mainStructure = drawDefinition?.structures?.find(
    (s: any) => s.stage === MAIN && s.stageSequence === 1,
  );
  if (!mainStructure) return;

  const qualifierSlots = (mainStructure.positionAssignments ?? []).filter(
    (p: any) => p?.qualifier === true,
  ).length;
  if (qualifierSlots <= 0) return;

  const hasQualifyingStructure = (drawDefinition?.structures ?? []).some(
    (s: any) => s.stage === QUALIFYING,
  );
  if (hasQualifyingStructure) return;

  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.className = 'tmx-generate-qualifying-banner notification is-info is-light';

  const message = document.createElement('span');
  message.textContent = `${qualifierSlots} qualifier ${qualifierSlots === 1 ? 'position' : 'positions'} reserved — no qualifying structure exists.`;
  banner.appendChild(message);

  const button = document.createElement('button');
  button.className = 'button is-info is-small';
  button.style.marginLeft = '0.75rem';
  button.textContent = 'Generate qualifying';
  button.onclick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    addDraw({
      callback: (result: any) => {
        const structureId = result.drawDefinition?.structures?.find(
          ({ stage }: any) => stage === QUALIFYING,
        )?.structureId;
        if (typeof callback === 'function') callback();
        navigateToEvent({ eventId, drawId, structureId, renderDraw: true });
      },
      isQualifying: true,
      drawName,
      eventId,
      drawId,
    } as any);
  };
  banner.appendChild(button);

  // Prepend so it sits above the bracket render.
  container.insertBefore(banner, container.firstChild);
}
